import {
  Button,
  EmptyState,
  InlineAlert,
  Spinner,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { PackageIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useCompaniesContent } from '../../content/sections/companies.js';
import { useOrgPackages } from '../../hooks/use-packs-catalog.js';
import { fmtDate } from '../../lib/format.js';
import { SegmentedControl } from '../filters/segmented-control.js';
import { SearchInput } from '../search-input.js';

/* Packages org-private autorizados a uma empresa (aba Companies, ADR-0400+, lote D5).
   Read-only (observabilidade de staff): o recorte por orgId é feito server-side no
   admin-service. Busca por nome/id + facetas de verificação e versão ativa são filtros
   client-side sobre o array já carregado. Copy bilíngue via `useCompaniesContent` (ADR-0811). */
type VerifiedFilter = 'all' | 'verified';
type ActiveFilter = 'all' | 'active';

export function CompanyPackagesTab({ companyId }: { readonly companyId: string }) {
  const copy = useCompaniesContent();
  const { data, isLoading, isError } = useOrgPackages(companyId);
  const packs = useMemo(() => data ?? [], [data]);

  const [search, setSearch] = useState('');
  const [verified, setVerified] = useState<VerifiedFilter>('all');
  const [active, setActive] = useState<ActiveFilter>('all');

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return packs.filter((pack) => {
      const matchesSearch =
        query === '' ||
        pack.displayName.toLowerCase().includes(query) ||
        pack.id.toLowerCase().includes(query);
      const matchesVerified = verified === 'all' || pack.verified;
      const matchesActive = active === 'all' || pack.latestActive !== null;
      return matchesSearch && matchesVerified && matchesActive;
    });
  }, [packs, search, verified, active]);

  if (isError) {
    return <InlineAlert variant="destructive">{copy.packagesTab.loadError}</InlineAlert>;
  }
  if (isLoading) {
    return (
      <div className="grid place-items-center p-12">
        <Spinner size="lg" />
      </div>
    );
  }
  if (packs.length === 0) {
    return (
      <EmptyState
        icon={<PackageIcon className="size-6" />}
        title={copy.packagesTab.emptyTitle}
        description={copy.packagesTab.emptyDescription}
      />
    );
  }

  const verifiedCount = packs.filter((pack) => pack.verified).length;
  const activeCount = packs.filter((pack) => pack.latestActive !== null).length;
  const clearFilters = (): void => {
    setSearch('');
    setVerified('all');
    setActive('all');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          ariaLabel={copy.packagesTab.searchLabel}
          placeholder={copy.packagesTab.searchPlaceholder}
        />
        <SegmentedControl<VerifiedFilter>
          ariaLabel={copy.packagesTab.verifiedFacetLabel}
          value={verified}
          onChange={setVerified}
          options={[
            { value: 'all', label: copy.packagesTab.optionAll, count: packs.length },
            { value: 'verified', label: copy.packagesTab.optionVerified, count: verifiedCount },
          ]}
        />
        <SegmentedControl<ActiveFilter>
          ariaLabel={copy.packagesTab.activeFacetLabel}
          value={active}
          onChange={setActive}
          options={[
            { value: 'all', label: copy.packagesTab.optionAll, count: packs.length },
            { value: 'active', label: copy.packagesTab.optionActive, count: activeCount },
          ]}
        />
        <span className="text-sm tabular-nums text-muted-foreground">
          {copy.common.countOf(filtered.length, packs.length)}
        </span>
      </div>
      {filtered.length === 0 ? (
        <EmptyState
          icon={<PackageIcon className="size-6" />}
          title={copy.common.noResultsTitle}
          description={copy.packagesTab.emptyFilteredDescription}
          primaryAction={
            <Button variant="outline" onClick={clearFilters}>
              {copy.common.clearFilters}
            </Button>
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{copy.packagesTab.columnPackage}</TableHead>
              <TableHead>{copy.packagesTab.columnAuthor}</TableHead>
              <TableHead>{copy.packagesTab.columnActiveVersion}</TableHead>
              <TableHead>{copy.packagesTab.columnVersions}</TableHead>
              <TableHead>{copy.packagesTab.columnUpdatedAt}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((pack) => (
              <TableRow key={pack.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">{pack.displayName}</span>
                    {pack.verified && (
                      <StatusBadge variant="success" dot>
                        {copy.packagesTab.verifiedBadge}
                      </StatusBadge>
                    )}
                  </div>
                  <div className="font-mono text-xs text-muted-foreground">{pack.id}</div>
                </TableCell>
                <TableCell className="text-muted-foreground">{pack.author}</TableCell>
                <TableCell>
                  {pack.latestActive === null ? (
                    <StatusBadge variant="warning">
                      {copy.packagesTab.noActiveVersionBadge}
                    </StatusBadge>
                  ) : (
                    <span className="font-mono text-xs text-muted-foreground">
                      v{pack.latestActive}
                    </span>
                  )}
                </TableCell>
                <TableCell numeric className="text-muted-foreground">
                  {pack.versionCount}
                </TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(pack.lastUpdated)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
